import { z } from 'zod';
import { DAY_COUNTING_MODES } from '../enums';
import { optionalEmail, optionalText, requiredText } from './common';

/**
 * Regla de días facturables de almacenaje.
 * La regla de negocio definitiva está PENDIENTE: `confirmed=false` hace que la
 * interfaz muestre un aviso hasta que un administrador la revise y confirme.
 */
export const billingRulesSchema = z
  .object({
    dayCountingMode: z.enum(DAY_COUNTING_MODES),
    /** Solo en días naturales: ¿se cobra el día de entrada? */
    countEntryDay: z.boolean(),
    /** Solo en días naturales: ¿se cobra el día de salida? */
    countExitDay: z.boolean(),
    /** Días mínimos facturables por estancia (0 = sin mínimo). */
    minimumDays: z.number().int().min(0).max(365),
    confirmed: z.boolean(),
  })
  .refine(
    (rules) =>
      rules.dayCountingMode !== 'CALENDAR_DAYS' || rules.countEntryDay || rules.countExitDay,
    {
      message: 'En días naturales debe contar al menos el día de entrada o el de salida',
      path: ['countEntryDay'],
    },
  );
export type BillingRules = z.infer<typeof billingRulesSchema>;

/**
 * Valor por defecto. Cuenta tanto el día de entrada como el de salida (así lo especificó el
 * cliente con un ejemplo: entra 29/09, sale 03/10 → 2 días en la liquidación de septiembre,
 * 3 días en la de octubre; solo cuadra si ambos días cuentan). Sigue marcado como no
 * confirmado hasta que un administrador lo revise y confirme en Configuración.
 */
export const DEFAULT_BILLING_RULES: BillingRules = {
  dayCountingMode: 'CALENDAR_DAYS',
  countEntryDay: true,
  countExitDay: true,
  minimumDays: 1,
  confirmed: false,
};

/** Datos de la empresa que aparecen en las liquidaciones. */
export const companySettingsSchema = z.object({
  name: optionalText(200),
  taxId: optionalText(30),
  address: optionalText(200),
  postalCode: optionalText(20),
  city: optionalText(100),
  province: optionalText(100),
  country: optionalText(100),
  phone: optionalText(50),
  email: optionalEmail,
  iban: optionalText(50),
});
export type CompanySettings = z.infer<typeof companySettingsSchema>;

export const DEFAULT_COMPANY_SETTINGS: CompanySettings = {
  name: null,
  taxId: null,
  address: null,
  postalCode: null,
  city: null,
  province: null,
  country: null,
  phone: null,
  email: null,
  iban: null,
};

/**
 * Configuración de los documentos de facturación. La app genera LIQUIDACIONES
 * (la factura legal se emite en el programa de contabilidad), por eso título,
 * serie e impuesto son configurables.
 */
export const invoicingSettingsSchema = z.object({
  documentTitle: requiredText(60),
  seriesPrefix: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{1,10}$/, 'Solo letras y números (máx. 10)'),
  taxLabel: requiredText(30),
  /** Tipo impositivo en puntos básicos (2100 = 21 %). */
  taxRateBp: z.number().int().min(0).max(10000),
  footerText: optionalText(1000),
});
export type InvoicingSettings = z.infer<typeof invoicingSettingsSchema>;

export const DEFAULT_INVOICING_SETTINGS: InvoicingSettings = {
  documentTitle: 'LIQUIDACIÓN DE SERVICIOS',
  seriesPrefix: 'L',
  taxLabel: 'IVA',
  taxRateBp: 2100,
  footerText: null,
};

export const SETTINGS_KEYS = ['company', 'billingRules', 'invoicing'] as const;
export type SettingsKey = (typeof SETTINGS_KEYS)[number];

export interface SettingsMap {
  company: CompanySettings;
  billingRules: BillingRules;
  invoicing: InvoicingSettings;
}

export const SETTINGS_SCHEMAS = {
  company: companySettingsSchema,
  billingRules: billingRulesSchema,
  invoicing: invoicingSettingsSchema,
} as const;

export const SETTINGS_DEFAULTS: SettingsMap = {
  company: DEFAULT_COMPANY_SETTINGS,
  billingRules: DEFAULT_BILLING_RULES,
  invoicing: DEFAULT_INVOICING_SETTINGS,
};
